import { useState, useEffect, useCallback } from 'react';
import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';
import notify from '../../../utils/notify';
import {
  buildIncotermRows,
  fetchIncotermTimeline,
  getDeliveryGroup,
  mapParcelFromSources,
  normalizeDeliveryItems,
  resolveId,
  unwrapDeliveryDetail,
} from '../utils/quantityDeliveryUtils';

// `notifyError` opt-in: callers that predate it keep the silent fallback.
const useQuantityDeliveries = (
  procurementId,
  { enabled = true, notifyError = false } = {}
) => {
  const [parcels, setParcels] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(Boolean(enabled && procurementId));

  const fetchParcels = useCallback(async () => {
    if (!procurementId || !enabled) return;

    const token = getAuthToken();
    if (!token) return;

    setIsLoading(true);
    setError(null);

    try {
      const listResponse = await fetch(
        `${api.API_URL}/quantity-delivery?ProcurementId=${procurementId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!listResponse.ok) {
        throw new Error('Failed to load quantity delivery');
      }

      const listResult = await listResponse.json();
      const deliveryItems = normalizeDeliveryItems(listResult?.data);

      const parcelDetails = await Promise.all(
        deliveryItems.map(async item => {
          const quantityDeliveryTermId = resolveId(item);
          if (!quantityDeliveryTermId) return null;

          const [detailResponse, deliveryDetailResponse] = await Promise.all([
            fetch(
              `${api.API_URL}/quantity-delivery/${quantityDeliveryTermId}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  Accept: 'application/json',
                },
              }
            ),
            fetch(
              `${api.API_URL}/procurement/get-delivery-detail?PageNumber=1&PageSize=100&procurementId=${procurementId}&quantityDeliveryTermId=${quantityDeliveryTermId}`,
              {
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
              }
            ),
          ]);

          let quantityDetail = item;
          if (detailResponse.ok) {
            const detailResult = await detailResponse.json();
            const unwrapped = unwrapDeliveryDetail(detailResult);
            if (resolveId(unwrapped)) {
              quantityDetail = {
                ...item,
                ...unwrapped,
                id: quantityDeliveryTermId,
              };
            }
          }

          let deliveryGroup = null;
          let incotermRows = [];
          let incotermTimelineItems = [];
          let deliveryDetailResult = null;

          if (deliveryDetailResponse.ok) {
            deliveryDetailResult = await deliveryDetailResponse.json();
            deliveryGroup = getDeliveryGroup(
              deliveryDetailResult,
              quantityDeliveryTermId
            );
          }

          const { rawItems, timelineItems } = await fetchIncotermTimeline(
            quantityDeliveryTermId,
            token
          );
          incotermTimelineItems = timelineItems;
          incotermRows = buildIncotermRows(deliveryDetailResult, rawItems);

          return {
            ...mapParcelFromSources(quantityDetail, deliveryGroup),
            incotermRows,
            incotermTimelineItems,
          };
        })
      );

      setParcels(parcelDetails.filter(Boolean));
    } catch (err) {
      setParcels([]);
      setError(err);
      if (notifyError) notify.apiError(err, 'load delivery units');
    } finally {
      setIsLoading(false);
    }
  }, [procurementId, enabled, notifyError]);

  useEffect(() => {
    fetchParcels();
  }, [fetchParcels]);

  return {
    parcels,
    isLoading: enabled ? isLoading : false,
    error,
    refresh: fetchParcels,
  };
};

export default useQuantityDeliveries;
